import React, { useState, useEffect, useCallback } from 'react';
import { GeneticCalculator } from './GeneticCalculator.tsx';
import { LoginContainer } from './Login.tsx';
import { Menu } from './Menu.tsx';
import { Browse } from './Browse.tsx';
import { BasicFetch } from '../utils/BasicFetch.ts';
import { Collection } from './collection/Collection.tsx';
import { CareSheet } from './CareSheet.tsx';
import { ProfileInfo } from './ProfileInfo.tsx';
import { Clutches } from './clutches/Clutches.tsx';
import { Intro } from './Intro.tsx';
import { Loading } from './Loading.tsx';
import { ReptoolModal } from './utilities/ReptoolModal.tsx';
import { BountyList } from './BountyList.tsx';
import { blankReptile, Reptile } from '../types/Reptile.ts';
import { CollectionReptile } from './collection/types/CollectionTypes.ts';
import { CollectionReptileModal } from './collection/modals/CollectionReptileModal.tsx';
import { AvailableList } from './store/AvailableList.tsx';
import { initAppNotifications, initWebNotifications } from '../notificationsFunctions.ts';
import { initializeApp } from "firebase/app";
import { getMessaging } from "firebase/messaging";
import { RepToolContext } from '../RepToolContext.tsx';

type Feature =
  "intro" |
  "login" |
  "menu" |
  "browse" |
  "collection" |
  "careSheet" |
  "geneCalc" |
  "profile" |
  "clutches" |
  "bountyList" |
  "shop"


export const ReptoolApp = () => {
  (window as any).loggedIn = true;
  const { removeLastControls, clearControls, profileInfo, setProfileInfo } = React.useContext(RepToolContext);
  const [activeFeature, setActiveFeature] = useState<Feature>(localStorage.getItem('activeFeature') || 'intro');
  const [showAlert, setShowAlert] = useState(false);
  const [fadeAlert, setFadeAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const [fetchingData, setFetchingData] = useState(true);
  const [selectedReptile, setSelectedReptile] = useState(localStorage.getItem('selectedReptile'));
  const [reptiles, setReptiles] = useState<Reptile[]>([]);
  const [genes, setGenes] = useState([]);
  const [morphs, setMorphs] = useState([]);
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [scannedReptile, setScannedReptile] = useState<CollectionReptile | null>(null);

  const backToLogin = () => {
    (window as any).loggedIn = false;
    setActiveFeature('login');

  }
  const backToMenu = () => {
    (window as any).loggedIn = true;
    setActiveFeature('menu');

  }
  const menuOrLogin = () => {
    if ((window as any).loggedIn) {
      backToMenu();
    } else {
      backToLogin();
    }
  }


  useEffect(() => {
    function isAndroid() {
      return /Android/i.test(navigator.userAgent);
    }
    function getNavigationBarHeight() {
      if (!isAndroid() || !window.visualViewport) return 0;

      const screenHeight = window.screen.height;
      const viewportHeight = window.visualViewport.height;

      return Math.max(50, screenHeight - viewportHeight);
    }
    function getStatusBarHeight() {
      if (!isAndroid() || !window.visualViewport) return 0;
      return 25;
    }
    function updateBottomPadding() {
      // Nav bar height = difference
      let navBarHeight = getNavigationBarHeight();

      // Optional clamp to avoid weird tall values
      navBarHeight = Math.min(navBarHeight, 200);
      // Set CSS variable
      document.documentElement.style.setProperty('--safe-top', getStatusBarHeight() + 'px');
      document.documentElement.style.setProperty('--safe-bottom', navBarHeight + 'px');
    }

    // Run on startup and whenever viewport changes
    document.addEventListener('deviceready', updateBottomPadding);
    window.addEventListener('resize', updateBottomPadding);
    window.addEventListener('orientationchange', updateBottomPadding);
    updateBottomPadding();
  }, [])

  const handleKeyDown = useCallback((e: Event | KeyboardEvent) => {
    if ((e as KeyboardEvent).key === 'Escape' || e.type === 'backbutton') {
        e.preventDefault();
        // get top level modal
      const modals = document.querySelectorAll('.modal');
      let topLevelModal: JSX.Element | null = null;
      // look for the biggest "modal-layer-x" value
      let highestlevel = -1;
      modals.forEach(modal => {
        modal.classList.forEach(className => {
          if (className.startsWith('modal-layer-')) {
            const level = parseInt(className.replace('modal-layer-', ''));
            if (level > highestlevel) {
              highestlevel = level;
              topLevelModal = modal as unknown as JSX.Element;
            }
          }
        });
      });
      // look for the first visible modal close button and click it
      const closeButton = document.getElementById(`close-${highestlevel}`);
      if (closeButton) {
        (closeButton as HTMLElement).click();
        removeLastControls();
        return;
      }

      const closeCanvas = document.querySelectorAll('.close-canvas');
      if (closeCanvas && closeCanvas.length) {
        (closeCanvas[closeCanvas.length - 1] as HTMLElement).click();
        return;
      }
      menuOrLogin();
      return;
    }
    // fill in barcode scanner input if no other input is focused
    const currentFocusedInput = document.querySelectorAll('input:focus, textarea:focus');
    const barcodeInput = document.getElementById('barcode-scan');
    const globalBarcodeInput = document.getElementById('global-barcode-scan');
    if ((!currentFocusedInput.length && barcodeInput) || document.activeElement?.id === 'barcode-scan') {
      document.getElementById('barcode-scan')?.focus();
    } else if ((!currentFocusedInput.length && globalBarcodeInput) || document.activeElement?.id === 'global-barcode-scan') {
      document.getElementById('global-barcode-scan')?.focus();
    }

  }, [])

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener('backbutton', handleKeyDown, false);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener('backbutton', handleKeyDown, false);
    };
  }, [])


  useEffect(() => {
    localStorage.setItem('activeFeature', activeFeature);
  }, [activeFeature])

  window.backToLogin = backToLogin;

  const getUserInfo = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}/user/get_user`,
      method: 'GET',
      onSuccess: (data) => {
        if (data.message === 'Bad token' && activeFeature !== 'intro') {
          backToLogin();
        }
        (window as any).currentUser = { ...data }
        setProfileInfo({ ...data });
      },
      onFail: () => {
        backToLogin();
      },
    });

  }

  const alert = (alertMessage) => {
    setAlertMessage(alertMessage);
    setShowAlert(true);
    setFadeAlert(false);
    setTimeout(() => {
      setFadeAlert(true);
    }, 2500);
    setTimeout(() => {
      setShowAlert(false);
    }, 3000)
  }
  (window as any).alert = alert;


  const getReptiles = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}/reptile/get`,
      method: 'GET',
      onSuccess: (data) => {
        if (data.message === 'Bad token') {
          backToLogin();
        } else {
          setReptiles(data);
          (window as any).reptiles = data;
        }
      }
    })
  }
  const getMorphs = () => {
    if (selectedReptile && selectedReptile !== 'default') {
      BasicFetch({
        url: `${(window as any).serverLocation}/morph/reptile/${selectedReptile}/get`,
        method: 'GET',
        onSuccess: (data) => {
          if (data.message === 'Bad token') {
            backToLogin();
          } else {
            setMorphs(data.morphs);
            setGenes(data.genes);
            setFetchingData(false)
          }
        },
        onFail: () => alert("Failed to get morph data. Please close and reopen the app."),
        onFinish: () => setFetchingData(false),
      })
    } else {
      setFetchingData(false);
    }
  }

  const fetchReptile = (text: string) => {
    // keep trimming text until it starts with http
    while (text && !text.startsWith("http")) {
      text = text.slice(1);
    }

    if (!text || !text.startsWith("http")) {
      setScannedBarcode("");
      return;
    }
    BasicFetch({
      url: text,
      extra: {
        data_only: 0
      },
      method: 'GET',
      onSuccess: (data) => {
        setScannedReptile(data);
        setScannedBarcode("");
      },
      onFail: () => alert("Failed to get data"),
      onFinish: () => setScannedBarcode(""),
    });
  }


  const getPhotos = (morphName, callBack = () => { }) => {
    return;
  }
  (window as any).getPhotos = (morphName) => getPhotos(morphName);

  useEffect(() => {
    getReptiles();
    getUserInfo();
  }, []);
  useEffect(() => {
    if (!profileInfo || !profileInfo.id) {
      return;
    }
    const firebaseConfig = {
      apiKey: "AIzaSyCAY3Q6exmKAc8hyuLk1_N84Na__KAX4eM",
      authDomain: "reptool-9ed90.firebaseapp.com",
      projectId: "reptool-9ed90",
      storageBucket: "reptool-9ed90.firebasestorage.app",
      messagingSenderId: "130102185140",
      appId: "1:130102185140:web:617c84f2dbc3ca7f1d5be1",
      measurementId: "G-ND647G1NDS"
    };
    if ((window as any).cordova) {
      document.addEventListener('deviceready', function () {
        initAppNotifications();
      });
    } else {
      const app = initializeApp(firebaseConfig);
      const messaging = getMessaging(app);
      initWebNotifications(messaging);
    }
  }, [profileInfo])

  useEffect(() => {
    if (selectedReptile) {
      setFetchingData(true);
      localStorage.setItem('selectedReptile', selectedReptile);
      getMorphs();
    } else {
      setFetchingData(false);
    }
  }, [selectedReptile])


  const rep = reptiles.find(reptile => reptile.accronym === selectedReptile) || blankReptile;

  return (
    <>
      {scannedReptile && profileInfo && <CollectionReptileModal
        propReptile={scannedReptile}
        morphs={morphs}
        genes={genes}
        userInfo={profileInfo}
        isSpecies={rep.controls?.isSpecies || false}
        closeModal={() => {
          setScannedReptile(null);
        }}
        readOnly={false}
      />

      }
      {
        fetchingData && activeFeature !== "menu" && activeFeature !== "login" && <div className='w-100 h-100 d-flex justify-content-center align-items-center'>
          <div className='m-auto'>
            <Loading />
          </div>
        </div>
      }
      {activeFeature === "intro" && <Intro skipIntro={menuOrLogin} />}
      {activeFeature === "login" && <ReptoolModal
        id="login-modal"
        modalBodyClass='px-0'
        fullSize
        body={
          <LoginContainer
            showMenu={() => {
              (window as any).loggedIn = true;
              getUserInfo();
              getReptiles();
              (window as any).refetchReptileSelect();
              if (morphs.length === 0) {
                getMorphs();
              }
              setActiveFeature('menu');
            }} />}
      />}
      <div className='container px-0 h-100'>
        {activeFeature === "menu" && profileInfo && <Menu
          loading={fetchingData}
          reptiles={reptiles}
          reptileChanged={(reptile) => setSelectedReptile(reptile)}
          selectedReptile={selectedReptile}
          openBrowse={() => { setActiveFeature('browse') }}
          openCareSheet={() => { setActiveFeature('careSheet') }}
          openCollection={() => { setActiveFeature('collection') }}
          openGeneCalc={() => { setActiveFeature('geneCalc') }}
          openProfile={() => { setActiveFeature('profile') }}
          openClutches={() => { setActiveFeature('clutches') }}
          openBountyList={() => { setActiveFeature('bountyList') }}
          openShop={() => setActiveFeature('shop')}
        />}
        {activeFeature === "collection" && profileInfo &&
          <Collection
            userInfo={profileInfo}
            changeReptile={setSelectedReptile}
            reptiles={reptiles}
            reptile={selectedReptile}
            morphs={morphs}
            genes={genes}
            backToMenu={() => { setActiveFeature('menu') }}
            getPhotos={getPhotos}
            rep={rep}
          />}
        {
          activeFeature === "clutches" && profileInfo && <Clutches
            backToMenu={() => { setActiveFeature('menu') }}
            morphs={morphs}
            genes={genes}
            userInfo={profileInfo}
          />
        }
        {activeFeature === "geneCalc" && <GeneticCalculator
          backToMenu={() => { setActiveFeature('menu') }}
          morphs={morphs}
          genes={genes}
        />}
        {activeFeature === "browse" && profileInfo && <Browse
          morphs={morphs}
          genes={genes}
          userInfo={profileInfo}
          backToMenu={() => { setActiveFeature('menu') }}
          readOnly={!profileInfo.admin}
          rep={rep}
        />}
        {activeFeature === "careSheet" && <CareSheet
          selectedReptile={selectedReptile}
          backToMenu={() => { setActiveFeature('menu') }}
        />}
        {activeFeature === "profile" && profileInfo && <ProfileInfo
          info={profileInfo}
          getUserInfo={getUserInfo}
          backToMenu={() => { setActiveFeature('menu') }}
          backToLogin={() => { setActiveFeature('login') }}
        />}
        {
          activeFeature === "shop" && profileInfo && <AvailableList userId={profileInfo.id} backToMenu={backToMenu} currentUser={profileInfo} />
        }
        {activeFeature === "bountyList" && <BountyList reptiles={reptiles} backToMenu={() => {
          setActiveFeature('menu')
        }} />}
        {showAlert && <div id="alert" className={`${fadeAlert ? 'fadeOut' : 'fadeIn'} animated float-button text-center super-float button`}>{alertMessage}</div>}
        <input id='global-barcode-scan' style={{
          height: 0,
          width: 0,
          position: 'absolute',
          top: -100,
          left: -100
        }} type='text' value={scannedBarcode} onChange={(e) => setScannedBarcode(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              fetchReptile(scannedBarcode);
            }
          }} />

      </div>
    </>
  )
}
